import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { withError } from '@/react-tools/react-query';
import axios, { parseAxiosError } from '@/portainer/services/axios/axios';

import {
  AlertRulesResponse,
  AlertRuleRow,
  AlertRuleEnvironmentsResponse,
} from '../../types';
import { queryKeys } from '../../queryKeys';

export type { AlertRuleEnvironmentsResponse };

export function useAlertRules<T = AlertRulesResponse>({
  select,
  refetchInterval,
}: {
  select?: (data: AlertRulesResponse) => T;
  refetchInterval?: number;
}) {
  return useQuery(queryKeys.rules(), async () => getAlertRules(), {
    ...withError('Unable to get alert rules'),
    select,
    refetchInterval,
  });
}

export function useAlertRule<T = AlertRuleRow>({
  id,
  select,
  refetchInterval,
}: {
  id: number;
  select?: (data: AlertRuleRow) => T;
  refetchInterval?: number;
}) {
  return useQuery(queryKeys.rule(id), async () => getAlertRule(id), {
    ...withError('Unable to get alert rule'),
    select,
    refetchInterval,
  });
}

export async function getAlertRules() {
  try {
    const { data } = await axios.get<AlertRulesResponse>(
      `/observability/alerting/rules`
    );
    return data;
  } catch (error) {
    throw parseAxiosError(error);
  }
}

export async function getAlertRule(id: number) {
  try {
    const { data } = await axios.get<AlertRuleRow>(
      `/observability/alerting/rules/${id}`
    );
    return data;
  } catch (error) {
    throw parseAxiosError(error);
  }
}

export async function updateAlertRule(alertRule: AlertRuleRow) {
  try {
    const { data } = await axios.put<AlertRuleRow>(
      `/observability/alerting/rules/${alertRule.id}`,
      { AlertingRule: alertRule }
    );
    return data;
  } catch (error) {
    throw parseAxiosError(error);
  }
}

export function useUpdateAlertRule(id?: number) {
  const queryClient = useQueryClient();

  return useMutation(updateAlertRule, {
    ...withError('Unable to update alert rule'),
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.rules());
      if (id) {
        queryClient.invalidateQueries(queryKeys.rule(id));
      }
    },
  });
}

export async function getAlertRuleEnvironments(id: number) {
  try {
    const { data } = await axios.get<AlertRuleEnvironmentsResponse>(
      `/observability/alerting/rules/${id}/environments`
    );
    return data;
  } catch (error) {
    throw parseAxiosError(error);
  }
}

export function useAlertRuleEnvironments(id: number) {
  return useQuery(
    queryKeys.environments(id),
    () => getAlertRuleEnvironments(id),
    { ...withError('Unable to get alert rule environments') }
  );
}
